/**
 * Collaborative boards (doc 147 §8.4, §9 Phase 4).
 *
 * Saved sets of pinned entities. Presentational — `boards` and the save/delete
 * callbacks are injected.
 */
import { useState } from 'react';
import type { Board } from '../state/atlasApiTypes.js';
import { navigate } from '../state/hashRouter.js';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/EmptyState.js';

export interface BoardsViewProps {
  boards: Board[];
  /** The user's currently-pinned entities — the seed for a new board. */
  pinned: string[];
  onSaveBoard: (name: string, entityIds: string[]) => Promise<void>;
  onDeleteBoard: (boardId: string) => Promise<void>;
}

export function BoardsView({
  boards,
  pinned,
  onSaveBoard,
  onDeleteBoard,
}: BoardsViewProps): React.JSX.Element {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const createBoard = async (): Promise<void> => {
    if (!name.trim()) {
      setStatus('Name the board first.');
      return;
    }
    if (pinned.length === 0) {
      setStatus('Pin some entities first — a board is a saved set of pins.');
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await onSaveBoard(name.trim(), pinned);
      setStatus(`Saved “${name.trim()}” with ${pinned.length} entit${pinned.length === 1 ? 'y' : 'ies'}.`);
      setName('');
    } catch {
      setStatus('Could not save the board.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article>
      <div className="entity-head">
        <Icon name="dashboard_customize" size="lg" label="Boards" />
        <h1 className="entity-title">Boards</h1>
      </div>

      <div className="card" style={{ marginBottom: 'var(--sp-lg)', display: 'flex', gap: 'var(--sp-sm)' }}>
        <input
          className="filter-chip"
          style={{ flex: 1 }}
          aria-label="New board name"
          placeholder={`New board from ${pinned.length} pinned entit${pinned.length === 1 ? 'y' : 'ies'}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="btn btn-primary" onClick={createBoard} disabled={busy}>
          {busy ? 'Saving…' : 'Save board'}
        </button>
        {busy && <span role="status" aria-label="Saving board" className="spinner" />}
      </div>
      {status && (
        <p role="status" style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)' }}>
          {status}
        </p>
      )}

      {boards.length === 0 ? (
        <EmptyState
          icon="dashboard_customize"
          title="No boards yet"
          detail="Pin entities, then save them as a named board to revisit or share."
        />
      ) : (
        boards.map((board) => (
          <div key={board.id} className="ref-row">
            <Icon name="dashboard" size="sm" />
            <span style={{ fontWeight: 700 }}>{board.name}</span>
            <span style={{ color: 'var(--text3)' }}>
              {board.entityIds.length} entit{board.entityIds.length === 1 ? 'y' : 'ies'}
            </span>
            <button
              className="ref-kind"
              style={{ marginLeft: 'auto' }}
              onClick={() => board.entityIds[0] && navigate({ kind: 'entity', id: board.entityIds[0] })}
            >
              open
            </button>
            <button
              className="ref-kind"
              style={{ color: 'var(--danger)' }}
              aria-label={`Delete board ${board.name}`}
              onClick={() => void onDeleteBoard(board.id)}
            >
              delete
            </button>
          </div>
        ))
      )}
    </article>
  );
}
